import { Flex, SkeletonText, Text } from '@chakra-ui/react'
import React from 'react'
import IconVerticalEllipsis from '~/assets/svgs/icon-ellipsis-vertical.svg?react'
import IconFullscreen from '~/assets/svgs/icon-fullscreen.svg?react'
import IconChevronleft from '~/assets/svgs/icon-left-chevron.svg?react'
import IconLink from '~/assets/svgs/icon-link.svg?react'
import { Close } from '~/components/compositional/QuickActions'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  DropdownActionButton,
  MangoButtonMenuItem,
} from '~/components/shared/Dropdown/DropdownActionButton'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { textStyleKeys } from '~/themes/textStyles'
import { Size } from '~/types/ThemeTypes'

import { formatBasicDateTimeString } from '@mangovoice/core'
import { toNounCasing } from '@mangovoice/core'
import { CallInstance } from '@mangovoice/types'

interface CallDetailsViewHeaderProps {
  callInstanceLoading: boolean
  callInstance?: CallInstance
  isModal: boolean
  onClosed?: () => void
  handleCopyLink: (copyLinkAtTimestamp?: boolean) => void
  onOpenModal?: () => void
  shouldBeClosed?: boolean
  onDownloadAudio?: () => void
}

export const CallDetailsViewHeader = ({
  callInstanceLoading,
  callInstance,
  isModal,
  onClosed,
  handleCopyLink,
  onOpenModal,
  onDownloadAudio,
}: CallDetailsViewHeaderProps) => {
  const dropdownItems: MangoButtonMenuItem<string>[] = [
    {
      value: 'copy-link',
      content: 'Copy Link to Call',
      onSelect: () => handleCopyLink(),
    },
    {
      value: 'copy-link-at-timestamp',
      content: 'Copy Link to Call at Timestamp',
      onSelect: () => handleCopyLink(true),
    },
    {
      value: 'download-audio',
      content: 'Download audio',
      onSelect: () => onDownloadAudio?.(),
    },
  ]

  const hasAudio = !!callInstance?.recording_url

  return (
    <Flex
      data-testid="call-logs-detail-view-header"
      height="4.5rem"
      py="1.25rem"
      px="1.25rem"
      justifyContent="space-between"
      alignItems="center"
      alignSelf="stretch"
      bg="gray.100"
      borderBottom="2px solid"
      borderColor="gray.400"
      flex="0 1 auto"
    >
      <Flex alignItems="center">
        {!isModal && onClosed && (
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_100}
            tooltipText="Close Panel"
            actionIcon={IconChevronleft}
            onClick={onClosed}
            mr="0.75rem"
          />
        )}
        <Flex
          data-testid="call-logs-detail-view-header-title"
          direction="column"
          justifyContent="center"
          alignItems="flex-start"
        >
          <Text textStyle={textStyleKeys.H4}>Call Details</Text>
          {callInstanceLoading ? (
            <SkeletonText
              noOfLines={1}
              skeletonHeight="1rem"
              py="0.125rem"
              width="15rem"
            />
          ) : (
            <Text textStyle={textStyleKeys.Body2} color="gray.800">
              {toNounCasing(callInstance!.direction)} Call on{' '}
              {formatBasicDateTimeString(callInstance!.started_at)}
            </Text>
          )}
        </Flex>
      </Flex>
      {!isModal && !callInstanceLoading && onOpenModal && (
        <Flex
          data-testid="call-logs-detail-view-header-quickactions"
          alignItems="center"
          gap="0.75rem"
        >
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_200}
            tooltipText="Expand"
            actionIcon={IconFullscreen}
            onClick={onOpenModal}
          />
          {hasAudio ? (
            <DropdownActionButton
              variant={QuickActionButtonVariants.Default_200}
              tooltipText="More Actions"
              icon={IconVerticalEllipsis}
              items={dropdownItems}
              menuOptions={{
                width: '20rem',
              }}
            />
          ) : (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_200}
              tooltipText="Copy Link to Call"
              actionIcon={IconLink}
              onClick={() => handleCopyLink()}
            />
          )}
        </Flex>
      )}
      {isModal && (
        <Flex
          data-testid="call-logs-detail-view-header-quickactions"
          alignItems="center"
          gap="0.75rem"
        >
          {hasAudio ? (
            <DropdownActionButton
              variant={QuickActionButtonVariants.Default_200}
              tooltipText="More Actions"
              icon={IconVerticalEllipsis}
              items={dropdownItems}
              menuOptions={{
                width: '20rem',
              }}
            />
          ) : (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_200}
              tooltipText="Copy Link to Call"
              actionIcon={IconLink}
              onClick={() => handleCopyLink()}
            />
          )}
          <Close
            size={Size.sm}
            iconProps={{ boxSize: '0.63rem' }}
            onClick={onClosed}
          />
        </Flex>
      )}
    </Flex>
  )
}
